import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Users, UserMinus, Airplane, Cake, Trophy, Warning, Confetti, CalendarBlank,
  ClipboardText, ArrowsClockwise, Plus, MagnifyingGlass, CalendarPlus,
} from "@phosphor-icons/react";
import { workforceApi, formatDate } from "./workforceApi";
import CoverageWidget from "./CoverageWidget";

/**
 * WF-103 · Workforce Operations Centre
 *
 * PO directive 2026-02-04 — Not an HR homepage. Every tile answers a
 * management question. Five tile groups:
 *   1. Today's Workforce      — Who is working / who is away?
 *   2. Upcoming Events        — What's coming up in the next 30 days?
 *   3. Management Attention   — What needs my decision?
 *   4. Quick Statistics       — Workforce shape at a glance.
 *   5. Quick Actions          — Frictionless entry to the main workflows.
 *
 * Single aggregate fetch → `/workforce/dashboard/operations`.
 */
export default function OperationsDashboardTab({ onNavigateTab, onOpenEmployee }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await workforceApi.operationsDashboard();
      setData(r.data);
      setError(null);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not load Workforce dashboard");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading && !data) {
    return <div className="p-6 text-sm text-slate-500">Loading operations…</div>;
  }
  if (error) {
    return (
      <div className="rounded-md border border-status-error-bg bg-status-error-bg p-4 text-sm text-status-error-fg">
        {error}
      </div>
    );
  }
  if (!data) return null;

  const { today, upcoming_events, management_attention, quick_statistics } = data;

  return (
    <div data-testid="workforce-operations-dashboard" className="space-y-6">
      <div className="flex justify-between">
        <p className="text-xs text-slate-500">
          As of {formatDate(data.as_of)}
        </p>
        <button
          data-testid="wf-ops-refresh"
          onClick={load}
          className="inline-flex items-center gap-1 rounded-md border border-slate-300 px-2 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50"
        >
          <ArrowsClockwise size={11} weight="bold" /> Refresh
        </button>
      </div>

      {/* 1. TODAY'S WORKFORCE — Who is working today? Who is away? */}
      <TileGroup title="Today's Workforce" question="Who is working today? Who is away?" testid="wf-ops-today">
        <StatTile testid="wf-ops-working-today" icon={<Users size={18} />} label="Working Today" value={today.working_today_count} tone="emerald" />
        <StatTile testid="wf-ops-away-today"    icon={<UserMinus size={18} />} label="Away Today" value={today.away_today_count} tone={today.away_today_count > 0 ? "amber" : "slate"} />
        <StatTile testid="wf-ops-vacation-today" icon={<Airplane size={18} />} label="On Vacation" value={today.on_vacation_count} tone="sky" />
        <StatTile testid="wf-ops-time-off-upcoming" icon={<CalendarPlus size={18} />} label="Upcoming Time Off (30d)" value={today.upcoming_time_off?.length ?? 0} tone="violet" />
      </TileGroup>

      {/* WF-107E · Operational Coverage widget */}
      <CoverageWidget onNavigateTab={onNavigateTab} />

      {/* 2. UPCOMING EVENTS — What's coming up? */}
      <TileGroup title="Upcoming Events" question="What events are coming up?" testid="wf-ops-upcoming">
        <ListTile
          testid="wf-ops-birthdays"
          icon={<Cake size={18} />}
          label="Birthdays"
          items={upcoming_events.birthdays}
          empty="No birthdays in the next 30 days."
          render={(b) => (
            <button
              onClick={() => onOpenEmployee?.(b.employee_id)}
              className="flex w-full items-center justify-between gap-2 text-left hover:text-slate-900"
            >
              <span className="truncate">{b.full_name}</span>
              <span className="whitespace-nowrap font-mono text-[11px] text-slate-500">
                {formatDate(b.next_date)} · in {b.days_until}d
              </span>
            </button>
          )}
        />
        <ListTile
          testid="wf-ops-anniversaries"
          icon={<Confetti size={18} />}
          label="Anniversaries"
          items={upcoming_events.anniversaries}
          empty="No anniversaries in the next 30 days."
          render={(a) => (
            <button
              onClick={() => onOpenEmployee?.(a.employee_id)}
              className="flex w-full items-center justify-between gap-2 text-left hover:text-slate-900"
            >
              <span className="truncate">{a.full_name} · {a.years_completed}y</span>
              <span className="whitespace-nowrap font-mono text-[11px] text-slate-500">
                {formatDate(a.next_date)} · in {a.days_until}d
              </span>
            </button>
          )}
        />
        <ListTile
          testid="wf-ops-holidays"
          icon={<CalendarBlank size={18} />}
          label="Company Holidays"
          items={upcoming_events.company_holidays}
          empty="No company holidays in the next 30 days."
          render={(h) => (
            <div className="flex w-full items-center justify-between gap-2">
              <span className="truncate">{h.name}</span>
              <span className="whitespace-nowrap font-mono text-[11px] text-slate-500">{formatDate(h.date)}</span>
            </div>
          )}
        />
      </TileGroup>

      {/* 3. MANAGEMENT ATTENTION — What needs a decision from me? */}
      <TileGroup title="Management Attention" question="What needs my decision?" testid="wf-ops-attention">
        <ListTile
          testid="wf-ops-pending"
          icon={<ClipboardText size={18} />}
          label="Pending Requests"
          badge={management_attention.pending_requests.length}
          badgeTone={management_attention.pending_requests.length > 0 ? "amber" : "slate"}
          items={management_attention.pending_requests}
          empty="No pending requests."
          render={(r) => (
            <div className="flex w-full items-center justify-between gap-2">
              <span className="truncate">{r.employee_name ?? r.employee_id}</span>
              <span className="whitespace-nowrap font-mono text-[11px] text-slate-500">{formatDate(r.start_date)}</span>
            </div>
          )}
          onSeeAll={() => onNavigateTab?.("events")}
        />
        <ListTile
          testid="wf-ops-missing-info"
          icon={<Warning size={18} />}
          label="Missing Employee Information"
          badge={management_attention.missing_information.count}
          badgeTone={management_attention.missing_information.count > 0 ? "amber" : "slate"}
          items={management_attention.missing_information.employees}
          empty="Every active employee has complete information."
          render={(e) => (
            <button
              onClick={() => onOpenEmployee?.(e.employee_id)}
              className="flex w-full items-center justify-between gap-2 text-left hover:text-slate-900"
            >
              <span className="truncate">{e.full_name}</span>
              <span className="whitespace-nowrap text-[11px] text-slate-500">{e.missing_fields.length} missing</span>
            </button>
          )}
        />
        <PlaceholderTile
          testid="wf-ops-vacation-flags"
          icon={<Airplane size={18} />}
          label="Vacation Balances to Review"
          caption={management_attention.vacation_balances_flagged_caption || "Awaiting Vacation Management Engine"}
        />
      </TileGroup>

      {/* 4. QUICK STATISTICS — workforce shape at a glance. */}
      <TileGroup title="Quick Statistics" question="What does the workforce look like today?" testid="wf-ops-stats">
        <StatTile testid="wf-ops-total-active" icon={<Users size={18} />} label="Active Employees" value={quick_statistics.total_active_employees} tone="slate" />
        <BreakdownTile
          testid="wf-ops-by-workgroup"
          icon={<Users size={18} />}
          label="By Work Group"
          entries={Object.entries(quick_statistics.employees_by_work_group)}
        />
        <StatTile testid="wf-ops-avg-years" icon={<Trophy size={18} />} label="Avg Years of Service" value={quick_statistics.average_years_of_service ?? "—"} tone="slate" suffix={quick_statistics.average_years_of_service !== null ? "yrs" : ""} />
        <MilestoneTile testid="wf-ops-longest" icon={<Trophy size={18} />} label="Longest Serving" milestone={quick_statistics.longest_serving} onClick={onOpenEmployee} suffix="yrs" />
        <MilestoneTile testid="wf-ops-newest" icon={<Confetti size={18} />} label="Newest Employee" milestone={quick_statistics.newest_employee} onClick={onOpenEmployee} hireLabel />
      </TileGroup>

      {/* 5. QUICK ACTIONS — frictionless entry. Frontend only. */}
      <TileGroup title="Quick Actions" question="What action can I take right now?" testid="wf-ops-actions">
        <ActionTile testid="wf-ops-action-add-employee"    icon={<Plus size={18} />} label="Add Employee"       onClick={() => { onNavigateTab?.("employees"); toast.info("Open the Employees tab and click 'New Employee'.") ;}} />
        <ActionTile testid="wf-ops-action-request-time-off" icon={<CalendarPlus size={18} />} label="Request Time Off" onClick={() => { onNavigateTab?.("events"); toast.info("Open the Events tab and click 'New Event'."); }} />
        <ActionTile testid="wf-ops-action-calendar"        icon={<CalendarBlank size={18} />} label="Company Calendar" onClick={() => onNavigateTab?.("calendar")} />
        <ActionTile testid="wf-ops-action-directory"       icon={<MagnifyingGlass size={18} />} label="Employee Directory" onClick={() => onNavigateTab?.("employees")} />
      </TileGroup>
    </div>
  );
}

// ---------- Building blocks ----------

function TileGroup({ title, question, children, testid }) {
  return (
    <section data-testid={testid}>
      <div className="mb-2 flex items-baseline justify-between">
        <h3 className="font-display text-sm font-semibold uppercase tracking-wider text-slate-500">{title}</h3>
        <span className="text-[11px] italic text-slate-400">{question}</span>
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {children}
      </div>
    </section>
  );
}

const TONE = {
  slate:   { border: "border-slate-200",  bg: "bg-white", accent: "text-slate-900" },
  emerald: { border: "border-emerald-200", bg: "bg-emerald-50", accent: "text-emerald-800" },
  amber:   { border: "border-amber-200",  bg: "bg-amber-50",  accent: "text-amber-800" },
  sky:     { border: "border-sky-200",    bg: "bg-sky-50",    accent: "text-sky-800" },
  violet:  { border: "border-violet-200", bg: "bg-violet-50", accent: "text-violet-800" },
};

function StatTile({ testid, icon, label, value, tone = "slate", suffix }) {
  const t = TONE[tone] || TONE.slate;
  return (
    <div data-testid={testid} className={`rounded-lg border ${t.border} ${t.bg} p-4 shadow-sm`}>
      <div className="mb-1 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
        {icon} {label}
      </div>
      <div className={`font-mono text-3xl font-semibold ${t.accent}`}>
        {value}{suffix ? <span className="ml-1 text-sm font-medium text-slate-500">{suffix}</span> : null}
      </div>
    </div>
  );
}

function ListTile({ testid, icon, label, items = [], empty, render, badge, badgeTone = "slate", onSeeAll }) {
  const tb = TONE[badgeTone] || TONE.slate;
  return (
    <div data-testid={testid} className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
      <div className="mb-2 flex items-center justify-between">
        <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
          {icon} {label}
        </div>
        {typeof badge === "number" && (
          <span className={`rounded-full border ${tb.border} ${tb.bg} px-2 py-0.5 text-[10px] font-semibold ${tb.accent}`}>
            {badge}
          </span>
        )}
      </div>
      {items.length === 0 ? (
        <p className="text-xs text-slate-400">{empty}</p>
      ) : (
        <ul className="space-y-1 text-sm text-slate-700">
          {items.slice(0, 5).map((it, i) => (
            <li key={i} className="border-b border-slate-100 pb-1 last:border-b-0">{render(it)}</li>
          ))}
          {items.length > 5 && (
            <li className="text-[11px] text-slate-400">+ {items.length - 5} more</li>
          )}
        </ul>
      )}
      {onSeeAll && items.length > 0 && (
        <button
          onClick={onSeeAll}
          className="mt-2 text-[11px] font-semibold text-slate-600 hover:text-slate-900"
        >
          See all →
        </button>
      )}
    </div>
  );
}

function PlaceholderTile({ testid, icon, label, caption }) {
  return (
    <div data-testid={testid} className="rounded-lg border border-dashed border-slate-300 bg-slate-50 p-4">
      <div className="mb-1 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
        {icon} {label}
      </div>
      <p className="text-xs text-slate-500">{caption}</p>
    </div>
  );
}

function BreakdownTile({ testid, icon, label, entries }) {
  return (
    <div data-testid={testid} className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
      <div className="mb-2 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
        {icon} {label}
      </div>
      {entries.length === 0 ? (
        <p className="text-xs text-slate-400">No employees assigned.</p>
      ) : (
        <ul className="space-y-1 text-sm">
          {entries.sort((a, b) => b[1] - a[1]).map(([wg, n]) => (
            <li key={wg} className="flex items-center justify-between">
              <span className="truncate text-slate-700">{wg}</span>
              <span className="font-mono font-semibold text-slate-900">{n}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function MilestoneTile({ testid, icon, label, milestone, onClick, suffix, hireLabel }) {
  return (
    <div data-testid={testid} className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
      <div className="mb-2 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
        {icon} {label}
      </div>
      {!milestone ? (
        <p className="text-xs text-slate-400">Not enough data.</p>
      ) : (
        <button
          onClick={() => onClick?.(milestone.employee_id)}
          className="block w-full text-left"
        >
          <div className="text-sm font-semibold text-slate-900">{milestone.full_name}</div>
          <div className="mt-0.5 text-[11px] text-slate-500">
            {hireLabel
              ? `Hired ${formatDate(milestone.hire_date)}`
              : `${milestone.years_of_service} ${suffix || ""}`}
          </div>
        </button>
      )}
    </div>
  );
}

function ActionTile({ testid, icon, label, onClick }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className="flex flex-col items-start gap-1 rounded-lg border border-slate-200 bg-white p-4 text-left shadow-sm transition hover:border-slate-300 hover:bg-slate-50"
    >
      <span className="rounded-md border border-slate-200 bg-slate-50 p-1.5 text-slate-700">{icon}</span>
      <span className="text-sm font-semibold text-slate-900">{label}</span>
    </button>
  );
}
